import { useId, useRef, useState } from 'react'

/**
 * The drag-and-drop upload block used three times on the Profile Form (photo,
 * CV, certificate).
 *
 * Form.cshtml wired these with inline `ondragover` / `ondrop` attributes calling
 * globals defined in form.js, one hardcoded copy per zone. Same markup and class
 * names so the template CSS applies; the handlers are local.
 *
 * Size and type limits are enforced here as they were in `handlePhotoFile` /
 * `handleCVFile`, and are also re-checked server-side.
 */
export interface FileUploadZoneProps {
  id: string
  label: string
  hint: string
  /** e.g. "JPG, JPEG, PNG up to 2MB" */
  fileInfo: string
  icon: string
  accept: string
  maxBytes: number
  file: File | null
  onFileChange: (file: File | null) => void
  /** Shown when a file already exists upstream but has not been re-attached. */
  existingName?: string | null
  invalid?: boolean
}

export function FileUploadZone({
  id,
  label,
  hint,
  fileInfo,
  icon,
  accept,
  maxBytes,
  file,
  onFileChange,
  existingName,
  invalid = false,
}: FileUploadZoneProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState('')
  const errorId = useId()

  function accepts(candidate: File): boolean {
    // `accept` is a comma-separated list of extensions and/or MIME patterns,
    // matched the same way the browser's own file picker would.
    return accept.split(',').some((rule) => {
      const token = rule.trim().toLowerCase()
      if (!token) return false
      if (token.startsWith('.')) return candidate.name.toLowerCase().endsWith(token)
      if (token.endsWith('/*')) return candidate.type.startsWith(token.slice(0, -1))
      return candidate.type.toLowerCase() === token
    })
  }

  function handleFile(candidate: File | null): void {
    setError('')
    if (!candidate) {
      onFileChange(null)
      return
    }
    if (!accepts(candidate)) {
      setError(`Invalid file type. Allowed: ${fileInfo}`)
      return
    }
    if (candidate.size > maxBytes) {
      setError(`File is too large. Maximum ${formatFileSize(maxBytes)}.`)
      return
    }
    onFileChange(candidate)
  }

  const chosenName = file?.name ?? existingName ?? ''

  return (
    <div className="upload-section">
      <label className="upload-label">{label}</label>

      <div
        className={`minimal-upload${isDragging ? ' dragover' : ''}${invalid ? ' is-invalid' : ''}`}
        id={`${id}UploadZone`}
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') inputRef.current?.click()
        }}
        onDragOver={(event) => {
          event.preventDefault()
          setIsDragging(true)
        }}
        onDragEnter={(event) => {
          event.preventDefault()
          setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setIsDragging(false)
          handleFile(event.dataTransfer.files[0] ?? null)
        }}
      >
        <div className="upload-icon">
          <i className={icon} />
        </div>
        <div className="upload-text">{hint}</div>
        <div className="upload-subtext">Click to browse or drag &amp; drop</div>
        <div className="file-info">{fileInfo}</div>
      </div>

      <input
        ref={inputRef}
        type="file"
        id={id}
        accept={accept}
        style={{ display: 'none' }}
        onChange={(event) => handleFile(event.target.files?.[0] ?? null)}
      />

      {chosenName && (
        <div className="uploaded-file" id={`${id}Uploaded`}>
          <span className="file-name">{chosenName}</span>
          {file && <span className="file-size">{formatFileSize(file.size)}</span>}
          <div className="file-success">
            <i className="fas fa-check-circle" />{' '}
            {file ? 'Uploaded successfully!' : 'Previously uploaded'}
          </div>
          <span
            className="remove-file"
            role="button"
            tabIndex={0}
            title="Remove"
            onClick={(event) => {
              event.stopPropagation()
              handleFile(null)
              if (inputRef.current) inputRef.current.value = ''
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleFile(null)
            }}
          >
            ×
          </span>
        </div>
      )}

      {error && (
        <div className="alert alert-danger mt-2" role="alert" id={errorId}>
          {error}
        </div>
      )}
    </div>
  )
}

/** `ProfileForm.formatFileSize` */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const units = ['Bytes', 'KB', 'MB', 'GB']
  const index = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${Number.parseFloat((bytes / Math.pow(1024, index)).toFixed(2))} ${units[index]}`
}
